/**
 * FEAT-APR08-002 — Score Approval Queue
 *
 * Owner-always / coach-with-can_approve_scores view of pending member scores
 * at the requester's gym. Pulls from GET /affiliates/scores/pending-approval
 * and posts approve/reject decisions to PATCH /scores/:id/approval.
 *
 * Access is enforced server-side by `requireCoachPermission('can_approve_scores')`
 * — owners always pass, coaches only with the flag. The page is also gated in
 * the sidebar so coaches without the flag never see the entry.
 */

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useApi } from '@/hooks/useApi';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, ClipboardCheck, Loader2, XCircle } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

interface PendingScore {
  score_id: string;
  value: number;
  notes: string | null;
  created_at: string;
  approval_status: 'pending' | 'approved' | 'rejected';
  user_id: string;
  athlete_name: string;
  workout_id: string;
  workout_name: string;
  scoring_type: string | null;
}

function formatScore(value: number, scoringType: string | null): string {
  if (scoringType === 'time') {
    const mins = Math.floor(value / 60);
    const secs = value % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
  if (scoringType === 'rounds_reps') {
    const rounds = Math.floor(value / 1000);
    const reps = value % 1000;
    return `${rounds} + ${reps}`;
  }
  if (scoringType === 'distance') return `${value}m`;
  if (scoringType === 'calories') return `${value} cal`;
  return `${value}`;
}

export default function ScoreApprovalQueue() {
  const { api } = useApi();
  const queryClient = useQueryClient();
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['score-approval-queue'],
    queryFn: async () => {
      const res = await api.get<{ scores: PendingScore[] }>('/affiliates/scores/pending-approval');
      if (!res.success) throw new Error(res.error);
      return res.data;
    },
  });

  const decideMutation = useMutation({
    mutationFn: async (vars: {
      scoreId: string;
      status: 'approved' | 'rejected';
      reason?: string;
    }) => {
      const res = await api.patch<{ score: { id: string; approval_status: string } }>(
        `/scores/${vars.scoreId}/approval`,
        { status: vars.status, reason: vars.reason }
      );
      if (!res.success) throw new Error(res.error);
      return res.data;
    },
    onSuccess: (_data, vars) => {
      toast.success(`Score ${vars.status}`);
      queryClient.invalidateQueries({ queryKey: ['score-approval-queue'] });
      queryClient.invalidateQueries({ queryKey: ['member-leaderboard'] });
      setRejectingId(null);
      setRejectReason('');
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Could not update score');
    },
  });

  if (isLoading) {
    return (
      <div className="flex h-[40vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-destructive">Failed to load approval queue</p>
        </CardContent>
      </Card>
    );
  }

  const scores = data?.scores || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <ClipboardCheck className="h-7 w-7 text-primary" />
          Score Approvals
        </h1>
        <p className="text-muted-foreground">
          Validate member scores against Rx/scaled standards. Approved scores are highlighted on the
          leaderboard; rejected scores are hidden.
        </p>
      </div>

      {scores.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <ClipboardCheck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground font-medium">No pending scores</p>
            <p className="text-sm text-muted-foreground mt-2">
              Everything's been reviewed. New member scores will appear here for approval.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {scores.map((s) => (
            <Card key={s.score_id}>
              <CardHeader>
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <CardTitle className="text-lg">{s.athlete_name}</CardTitle>
                    <CardDescription>
                      {s.workout_name} ·{' '}
                      {new Date(s.created_at).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </CardDescription>
                  </div>
                  <Badge variant="outline" className="font-mono">
                    {formatScore(s.value, s.scoring_type)}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {s.notes && <p className="text-sm text-muted-foreground italic">"{s.notes}"</p>}

                {rejectingId === s.score_id ? (
                  <div className="space-y-2">
                    <textarea
                      className="w-full rounded-md border border-border bg-background p-2 text-sm"
                      rows={2}
                      placeholder="Reason for rejection (optional)"
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() =>
                          decideMutation.mutate({
                            scoreId: s.score_id,
                            status: 'rejected',
                            reason: rejectReason,
                          })
                        }
                        disabled={decideMutation.isPending}
                      >
                        Confirm reject
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setRejectingId(null);
                          setRejectReason('');
                        }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() =>
                        decideMutation.mutate({ scoreId: s.score_id, status: 'approved' })
                      }
                      disabled={decideMutation.isPending}
                    >
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setRejectingId(s.score_id)}
                      disabled={decideMutation.isPending}
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Reject
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
